import { decodeJwtPayload } from './encoding-decode.utils';

export type CredentialAuthMaterial = {
  headers: Array<{ key: string; value: string }>;
  cookies: Array<{ name: string; value: string }>;
};

export const TOKEN_BEARING_HEADERS = [
  'authorization',
  'x-auth-token',
  'x-access-token',
  'x-api-key',
  'api-key',
  'bearer',
];

export const JWT_SYNC_HEADERS = [
  'authorization',
  'x-auth-token',
  'x-access-token',
];

const AUTH_SCHEME_PATTERN =
  /^(Bearer|Basic|Digest|HOBA|Mutual|Negotiate|OAuth|SCRAM-SHA-1|SCRAM-SHA-256|vapid)\s+/i;

export function buildCookieHeader(
  cookies: Array<{ name: string; value: string }>,
): string {
  return cookies.map((cookie) => `${cookie.name}=${cookie.value}`).join('; ');
}

export function extractAuthScheme(headerValue: string): string | null {
  const match = headerValue.match(AUTH_SCHEME_PATTERN);

  return match ? match[1] : null;
}

export type AuthorizationBodyShape =
  | { kind: 'encoded_json' }
  | { kind: 'b64' }
  | { kind: 'literal'; length: number };

export type AuthorizationShape = {
  scheme: string | null;
  body: AuthorizationBodyShape;
};

function stripAuthScheme(value: string): string {
  const scheme = extractAuthScheme(value);

  if (!scheme) {
    return value.trim();
  }

  return value.slice(scheme.length).trimStart();
}

function isStandardBase64(value: string): boolean {
  return (
    /^[A-Za-z0-9+/]+={0,2}$/.test(value) &&
    value.length % 4 === 0 &&
    value.length >= 8 &&
    /[+/=]/.test(value)
  );
}

function classifyAuthorizationBody({
  scheme,
  body,
}: {
  scheme: string | null;
  body: string;
}): AuthorizationBodyShape {
  if (body.length === 0) {
    return { kind: 'literal', length: 0 };
  }

  if (isJwt(body) && decodeJwtPayload(body) !== null) {
    return { kind: 'encoded_json' };
  }

  if (scheme?.toLowerCase() === 'basic' || isStandardBase64(body)) {
    return { kind: 'b64' };
  }

  return { kind: 'literal', length: body.length };
}

export function describeAuthorizationShape(
  authorizationValue: string,
): AuthorizationShape {
  const scheme = extractAuthScheme(authorizationValue);
  const body = stripAuthScheme(authorizationValue);

  return {
    scheme,
    body: classifyAuthorizationBody({ scheme, body }),
  };
}

export function formatAuthorizationShape(authorizationValue: string): string {
  const shape = describeAuthorizationShape(authorizationValue);
  const bodyLabel =
    shape.body.kind === 'encoded_json'
      ? 'encoded_json'
      : shape.body.kind === 'b64'
        ? 'b64'
        : `literal${shape.body.length}`;

  return shape.scheme ? `${shape.scheme} {${bodyLabel}}` : `{${bodyLabel}}`;
}

export function preserveAuthScheme({
  originalValue,
  newValue,
}: {
  originalValue: string;
  newValue: string;
}): string {
  if (extractAuthScheme(newValue)) {
    return newValue;
  }

  if (isEncryptedCredentialValue(newValue)) {
    return newValue;
  }

  const originalScheme = extractAuthScheme(originalValue);

  if (originalScheme) {
    return `${originalScheme} ${newValue}`;
  }

  return newValue;
}

export function isJwt(value: string): boolean {
  return /^[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}$/.test(
    value.trim(),
  );
}

export function stripBearerPrefix(value: string): string {
  return value.replace(/^Bearer\s+/i, '').trim();
}

export function isEncryptedCredentialValue(value: string): boolean {
  const stripped = stripBearerPrefix(value);

  return stripped.startsWith('v1.') || stripped.startsWith('v2.');
}

export function collectStoredJwtTokens({
  secrets,
  cookies,
  fullBrowserStorage,
}: {
  secrets: Array<{ key: string; value: string }>;
  cookies?: Array<{ name: string; value: string }>;
  fullBrowserStorage?: {
    localStorage: Array<{ key: string; value: string }>;
    sessionStorage: Array<{ key: string; value: string }>;
  };
}): Set<string> {
  const tokens = new Set<string>();
  const addToken = (value: string) => {
    const stripped = stripBearerPrefix(value);

    if (isJwt(stripped)) {
      tokens.add(stripped);
    }
  };

  for (const secret of secrets) {
    addToken(secret.value);
  }

  for (const cookie of cookies ?? []) {
    addToken(cookie.value);
  }

  for (const item of [
    ...(fullBrowserStorage?.localStorage ?? []),
    ...(fullBrowserStorage?.sessionStorage ?? []),
  ]) {
    addToken(item.value);
  }

  return tokens;
}

export function isStoredJwtSyncHeader({
  header,
  storedJwtTokens,
}: {
  header: { key: string; value: string };
  storedJwtTokens: Set<string>;
}): boolean {
  if (!JWT_SYNC_HEADERS.includes(header.key.toLowerCase())) {
    return true;
  }

  const headerToken = stripBearerPrefix(header.value);

  return isJwt(headerToken) && storedJwtTokens.has(headerToken);
}

export function enrichCredentialHeadersWithStoredJwtBearer({
  headers,
  secrets,
  fullBrowserStorage,
}: {
  headers: Array<{ key: string; value: string }>;
  secrets: Array<{ key: string; value: string }>;
  fullBrowserStorage?: {
    localStorage: Array<{ key: string; value: string }>;
    sessionStorage: Array<{ key: string; value: string }>;
  };
}): Array<{ key: string; value: string }> {
  const secretKeys = [
    ...secrets.map((secret) => secret.key),
    ...(fullBrowserStorage?.localStorage ?? []).map((item) => item.key),
    ...(fullBrowserStorage?.sessionStorage ?? []).map((item) => item.key),
  ];
  const secretsRecord = Object.fromEntries([
    ...secrets.map((secret) => [secret.key, secret.value] as const),
    ...(fullBrowserStorage?.localStorage ?? []).map(
      (item) => [item.key, item.value] as const,
    ),
    ...(fullBrowserStorage?.sessionStorage ?? []).map(
      (item) => [item.key, item.value] as const,
    ),
  ]);
  const existingAuthorization =
    headers.find((header) => header.key.toLowerCase() === 'authorization')
      ?.value ?? null;
  const bearer = resolveBearerAuthorizationFromSecrets({
    secrets: secretsRecord,
    secretKeys,
    existingAuthorizationValue: existingAuthorization,
  });

  if (!bearer) {
    return headers.map((header) =>
      header.key.toLowerCase() === 'authorization'
        ? {
            ...header,
            value: ensureBearerSchemeForJwtAuthorization(header.value),
          }
        : header,
    );
  }

  const authHeaderIndex = headers.findIndex(
    (header) => header.key.toLowerCase() === 'authorization',
  );

  if (authHeaderIndex === -1) {
    return [
      ...headers,
      {
        key: 'Authorization',
        value: ensureBearerSchemeForJwtAuthorization(bearer),
      },
    ];
  }

  return headers.map((header, index) =>
    index === authHeaderIndex
      ? {
          ...header,
          value: ensureBearerSchemeForJwtAuthorization(bearer),
        }
      : header.key.toLowerCase() === 'authorization'
        ? {
            ...header,
            value: ensureBearerSchemeForJwtAuthorization(header.value),
          }
        : header,
  );
}

function formatBearerAuthorization(token: string): string {
  if (isEncryptedCredentialValue(token)) {
    return token;
  }

  const stripped = stripBearerPrefix(token);

  return `Bearer ${stripped}`;
}

function collapseDuplicatedAuthScheme(authorizationValue: string): string {
  const scheme = extractAuthScheme(authorizationValue);

  if (!scheme) {
    return authorizationValue;
  }

  const remainder = authorizationValue.slice(scheme.length).trimStart();
  const repeatedScheme = extractAuthScheme(remainder);

  if (
    !repeatedScheme ||
    repeatedScheme.toLowerCase() !== scheme.toLowerCase()
  ) {
    return authorizationValue;
  }

  const token = remainder.slice(repeatedScheme.length).trimStart();
  const collapsed = token.length > 0 ? `${scheme} ${token}` : scheme;

  return collapseDuplicatedAuthScheme(collapsed);
}

export function ensureBearerSchemeForJwtAuthorization(
  authorizationValue: string,
): string {
  return collapseDuplicatedAuthScheme(authorizationValue);
}

export function normalizeAuthorizationHeaderValues(
  headers: Record<string, string>,
): Record<string, string> {
  const normalized = { ...headers };

  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === 'authorization') {
      normalized[key] = ensureBearerSchemeForJwtAuthorization(value);
    }
  }

  return normalized;
}

function scoreBearerSecretCandidate({
  key,
  value,
}: {
  key: string;
  value: string;
}): number {
  const lowerKey = key.toLowerCase();
  let score = 0;

  if (isEncryptedCredentialValue(value)) {
    return -100;
  }

  if (isJwt(stripBearerPrefix(value))) {
    score += 10;
  }

  if (/^token$|access[_-]?token|^access$/.test(lowerKey)) {
    score += 10;
  }

  if (/session[_-]?token|^session$/.test(lowerKey)) {
    score += 8;
  }

  if (/id[_-]?token/.test(lowerKey)) {
    score += 6;
  }

  if (/refresh/.test(lowerKey)) {
    score -= 5;
  }

  return score;
}

const MIN_BEARER_SECRET_SCORE = 10;

export function resolveBearerAuthorizationFromSecrets({
  secrets,
  secretKeys,
  existingAuthorizationValue,
}: {
  secrets: Record<string, string>;
  secretKeys: string[];
  existingAuthorizationValue?: string | null;
}): string | null {
  const candidates = secretKeys
    .filter((key) => {
      const value = secrets[key];

      return typeof value === 'string' && value.trim().length > 0;
    })
    .map((key) => ({
      key,
      value: secrets[key],
      score: scoreBearerSecretCandidate({ key, value: secrets[key] }),
    }))
    .sort((left, right) => right.score - left.score);

  const bestCandidate = candidates[0];

  if (!bestCandidate || bestCandidate.score < MIN_BEARER_SECRET_SCORE) {
    return null;
  }

  const existingToken = existingAuthorizationValue
    ? stripBearerPrefix(existingAuthorizationValue)
    : null;
  const candidateToken = stripBearerPrefix(bestCandidate.value);

  if (existingToken && existingToken === candidateToken) {
    if (
      existingAuthorizationValue &&
      isJwt(existingToken) &&
      !extractAuthScheme(existingAuthorizationValue)
    ) {
      return formatBearerAuthorization(existingAuthorizationValue);
    }

    return null;
  }

  if (
    existingAuthorizationValue &&
    !isJwt(stripBearerPrefix(existingAuthorizationValue))
  ) {
    return null;
  }

  if (isEncryptedCredentialValue(bestCandidate.value)) {
    return null;
  }

  return formatBearerAuthorization(bestCandidate.value);
}

function findHeaderEntry(
  headers: Record<string, string>,
  headerName: string,
): { key: string; value: string } | undefined {
  const target = headerName.toLowerCase();
  const entry = Object.entries(headers).find(
    ([key]) => key.toLowerCase() === target,
  );

  if (!entry) {
    return undefined;
  }

  return { key: entry[0], value: entry[1] };
}

function deleteHeaderEntry(
  headers: Record<string, string>,
  headerName: string,
): void {
  const target = headerName.toLowerCase();

  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === target) {
      delete headers[key];
    }
  }
}

export function applyCredentialMaterialToHeaders({
  existingHeaders,
  material,
  previousMaterial,
}: {
  existingHeaders: Record<string, string>;
  material: CredentialAuthMaterial;
  previousMaterial?: CredentialAuthMaterial;
}): Record<string, string> {
  const mergedHeaders = { ...existingHeaders };

  if (previousMaterial) {
    const newCredentialHeaderKeys = new Set(
      material.headers.map((header) => header.key.toLowerCase()),
    );

    if (material.cookies.length > 0) {
      newCredentialHeaderKeys.add('cookie');
    }

    for (const prevHeader of previousMaterial.headers) {
      if (!newCredentialHeaderKeys.has(prevHeader.key.toLowerCase())) {
        deleteHeaderEntry(mergedHeaders, prevHeader.key);
      }
    }

    if (
      previousMaterial.cookies.length > 0 &&
      !newCredentialHeaderKeys.has('cookie')
    ) {
      deleteHeaderEntry(mergedHeaders, 'cookie');
    }
  }

  for (const header of material.headers) {
    if (header.key.toLowerCase() === 'authorization') {
      const existingAuth = findHeaderEntry(mergedHeaders, 'authorization');
      const mergedAuthorizationValue = existingAuth
        ? preserveAuthScheme({
            originalValue: existingAuth.value,
            newValue: header.value,
          })
        : header.value;

      mergedHeaders[header.key] = ensureBearerSchemeForJwtAuthorization(
        mergedAuthorizationValue,
      );
      continue;
    }

    mergedHeaders[header.key] = header.value;
  }

  if (material.cookies.length > 0) {
    const cookieValue = buildCookieHeader(material.cookies);
    const existingCookie = findHeaderEntry(mergedHeaders, 'cookie');

    if (existingCookie) {
      mergedHeaders[existingCookie.key] = cookieValue;
    } else {
      mergedHeaders.Cookie = cookieValue;
    }
  }

  const credentialHeaderKeys = new Set(
    material.headers.map((header) => header.key.toLowerCase()),
  );
  const hasAnyTokenHeader = TOKEN_BEARING_HEADERS.some((name) =>
    credentialHeaderKeys.has(name),
  );

  if (!hasAnyTokenHeader && material.cookies.length > 0) {
    const tokenCookie = material.cookies.find((cookie) => isJwt(cookie.value));

    if (tokenCookie) {
      for (const [headerKey, headerValue] of Object.entries(mergedHeaders)) {
        if (JWT_SYNC_HEADERS.includes(headerKey.toLowerCase())) {
          mergedHeaders[headerKey] = preserveAuthScheme({
            originalValue: headerValue,
            newValue: tokenCookie.value,
          });
        }
      }
    }
  }

  return mergedHeaders;
}
